import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { TuiFileLike } from '@taiga-ui/kit';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { ModalConfirmComponent } from 'src/app/shared/modal-confirm/modal-confirm.component';
import { DeliveryNoteService } from 'src/app/services/delivery-note/delivery-note.service';
import { DeliveryNoteDocument } from 'src/app/models/delivery-note/delivery-note.model';
import { DeliveryNoteDocumentType } from 'src/app/enum/delivery-note/delivery-note-document-type.enum';

export interface DeliveryNoteModalResponse {
  documentId: string;
  documentName: string;
  uploadedDate: string;
  documentSize?: string;
}

@Component({
  selector: 'app-modal-upload-delivery-note',
  templateUrl: './modal-upload-delivery-note.component.html',
  styleUrls: ['./modal-upload-delivery-note.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ModalUploadDeliveryNoteComponent implements OnInit, OnDestroy {

  @Input() orderHash = '';
  @Input() serviceId = '';
  @Input() clientId = '';

  @ViewChild('searchInput') searchInput!: ElementRef;

  activeTabIndex = 0;
  files: File[] = [];
  // TuiFileLike y no File: los rechazados llevan `content`, que es lo que
  // `tui-file` pinta como motivo del error.
  rejected: TuiFileLike[] = [];
  // Mismo valor que marketplace y que MaxReceiveMessageSize del servidor gRPC.
  readonly maxFileSize = 50 * 1000 * 1000;
  saveAsPermanentFile = false;
  link_url = '';
  link_name = '';
  saveAsPermanentLink = false;

  savedFiles: DeliveryNoteDocument[] = [];
  private originalSavedFiles: DeliveryNoteDocument[] = [];

  addedFiles: DeliveryNoteDocument[] = [];
  private isSubmitting = false;
  addedLinks: DeliveryNoteDocument[] = [];
  showErrorAlert = false;
  isClosingAlert = false;
  textAlert = '';
  private errorAlertTimeout: any;
  DeliveryNoteDocumentType = DeliveryNoteDocumentType;

  private readonly i18n = {
    errorFileSameName: 'delivery-note.error_file_with_same_name',
    errorUploadingFile: 'delivery-note.error_uploading_file',
    errorDeletingFile: 'delivery-note.error_deleting_file',
    errorLinkSameName: 'delivery-note.error_link_with_same_name',
    errorUploadingLink: 'delivery-note.error_uploading_link',
    errorFetchingDocuments: 'delivery-note.error_fetching_documents',
    onlyOneFileAllowed: 'delivery-note.only_one_file_allowed',
    confirmDeleteSavedTitle: 'delivery-note.confirm_delete_saved_title',
    confirmDeleteSaved: 'delivery-note.confirm_delete_saved',
    fileTooLarge: 'delivery-note.file_too_large',
    onlyPdfAllowed: 'delivery-note.only_pdf_allowed',
  } as const;

  private static readonly BYTES_PER_MB = 1024 * 1024;
  private static readonly FILE_SIZE_DECIMALS = 2;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private deliveryNoteService: DeliveryNoteService,
    private translateService: TranslateService,
    private loader: LoaderService
  ) {}

  ngOnInit(): void {
    this.loadSavedFiles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.errorAlertTimeout) {
      clearTimeout(this.errorAlertTimeout);
    }
  }

  // --- File tab ---

  onChange(incoming: File[]): void {
    const newFiles = incoming
      .filter(f => !this.rejected.some(r => r.name === f.name))
      .filter(f => !this.files.includes(f));
    if (!newFiles.length) return;

    if (this.hasOneDocumentAlready || newFiles.length > 1) {
      this.showAlert(this.i18n.onlyOneFileAllowed);
      return;
    }

    // El fichero se sube al pulsar "Subir archivos", para que la casilla
    // "Guardar archivos para próximos servicios" se lea en ese momento.
    this.files.push(newFiles[0]);
  }

  onRemove(remove: File | TuiFileLike): void {
    this.files = this.files.filter(f => f !== remove);
    this.rejected = this.rejected.filter(f => f !== remove);
  }

  onReject(rejected: TuiFileLike | readonly TuiFileLike[]): void {
    const incoming = Array.isArray(rejected) ? rejected : [rejected as TuiFileLike];
    this.rejected = [...this.rejected, ...incoming.map(file => this.describeRejection(file))];
  }

  /**
   * `tui-file` pinta `content` cuando el estado es de error. Sin esto la fila
   * sale en rojo pero sin decir por qué se ha rechazado el fichero.
   */
  private describeRejection(file: TuiFileLike): TuiFileLike {
    const isTooLarge = (file.size ?? 0) > this.maxFileSize;
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      content: isTooLarge
        ? this.translateService.instant(this.i18n.fileTooLarge, { size: this.maxFileSizeInMb })
        : this.translateService.instant(this.i18n.onlyPdfAllowed),
    };
  }

  private get maxFileSizeInMb(): number {
    return this.maxFileSize / 1000 / 1000;
  }

  // Saved files (right panel)

  // Solo se puede adjuntar un documento por servicio, venga de una subida
  // nueva o de la lista de guardados.
  private get hasOneDocumentAlready(): boolean {
    return this.files.length > 0
      || this.originalSavedFiles.some(f => f.checked)
      || this.addedFiles.length > 0
      || this.addedLinks.length > 0;
  }

  isDocumentBlocked(file: DeliveryNoteDocument): boolean {
    return !file.checked && this.hasOneDocumentAlready;
  }

  deleteSavedFile(file: DeliveryNoteDocument, event: MouseEvent): void {
    // La fila entera alterna la selección; la papelera no debe hacerlo.
    event.stopPropagation();

    const confirmModal = this.modalService.open(ModalConfirmComponent, { centered: true });
    confirmModal.componentInstance.title = this.translateService.instant(this.i18n.confirmDeleteSavedTitle);
    confirmModal.componentInstance.message = this.translateService.instant(this.i18n.confirmDeleteSaved);

    confirmModal.result
      .then(() => {
        this.loader.show();
        this.deliveryNoteService
          .deleteFileOrLink(file.documentId)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.loader.hide())
          )
          .subscribe({
            next: () => {
              this.originalSavedFiles = this.originalSavedFiles.filter(f => f.documentId !== file.documentId);
              this.savedFiles = this.savedFiles.filter(f => f.documentId !== file.documentId);
            },
            error: () => this.showAlert(this.i18n.errorDeletingFile),
          });
      })
      .catch(() => { });
  }

  toggleSavedFile(file: DeliveryNoteDocument): void {
    if (this.isDocumentBlocked(file)) {
      this.showAlert(this.i18n.onlyOneFileAllowed);
      return;
    }
    file.checked = !file.checked;
  }

  onSearchChange(): void {
    const q = this.searchInput.nativeElement.value.toLowerCase();
    // Se desmarca todo, no solo lo visible: un guardado marcado que el filtro
    // deje fuera seguiría contando como documento adjunto.
    this.originalSavedFiles.forEach(f => f.checked = false);
    this.savedFiles = this.originalSavedFiles.filter(
      f => f.name.toLowerCase().includes(q) || (f.link && f.link.toLowerCase().includes(q))
    );
  }

  clearFilter(): void {
    this.searchInput.nativeElement.value = '';
    this.originalSavedFiles.forEach(f => f.checked = false);
    this.savedFiles = [...this.originalSavedFiles];
  }

  // Validation 

  disableButton(): boolean {
    const hasCheckedSaved = this.savedFiles.some(f => f.checked);
    return !hasCheckedSaved && this.files.length === 0 && !this.link_url && !this.link_name;
  }

  // Actions

  // Nada se sube hasta pulsar "Subir archivos", así que cancelar no deja
  // ficheros huérfanos que haya que borrar del backend.
  cancel(): void {
    this.activeModal.dismiss();
  }

  async submit(): Promise<void> {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      await this.uploadAndClose();
    } finally {
      this.isSubmitting = false;
    }
  }

  private async uploadAndClose(): Promise<void> {
    this.loader.show();

    if (this.link_name && this.link_url) {
      try {
        const result = await firstValueFrom(
          this.deliveryNoteService
            .uploadFileOrLink(!this.saveAsPermanentLink, this.link_name, this.link_url)
            .pipe(takeUntil(this.destroy$))
        );
        this.addedLinks.push({
          documentId: result.Id,
          name: result.PdfName,
          link: result.PdfUrl,
          type: DeliveryNoteDocumentType.Link,
          checked: false,
          isNew: true,
        });
      } catch (error: any) {
        this.loader.hide();
        if (error?.status === 409) {
          this.textAlert = this.translateService.instant(this.i18n.errorLinkSameName) + " '" + this.link_name + "'";
        } else if (error?.error?.error) {
          this.textAlert = error.error.error;
        } else {
          this.textAlert = this.translateService.instant(this.i18n.errorUploadingLink);
        }
        this.showErrorAlert = true;
        this.setErrorAlertAutoClose();
        return;
      }
    } else if (this.files.length > 0) {
      const file = this.files[0];
      try {
        const result = await firstValueFrom(
          this.deliveryNoteService
            .uploadFileOrLink(!this.saveAsPermanentFile, file.name, undefined, file)
            .pipe(takeUntil(this.destroy$))
        );
        const fileSize = (file.size / ModalUploadDeliveryNoteComponent.BYTES_PER_MB).toFixed(ModalUploadDeliveryNoteComponent.FILE_SIZE_DECIMALS) + ' MB';
        this.addedFiles.push({
          documentId: result.Id,
          name: result.PdfName,
          fileSize,
          type: DeliveryNoteDocumentType.Document,
          checked: false,
          isNew: true,
        });
      } catch (error: any) {
        this.loader.hide();
        if (error?.status === 409) {
          this.textAlert = this.translateService.instant(this.i18n.errorFileSameName) + " '" + file.name + "'";
        } else if (error?.error?.error) {
          // El proxy devuelve el motivo real, que suele ser más útil que el
          // genérico: por ejemplo el tope de tamaño de mensaje de gRPC.
          this.textAlert = error.error.error;
        } else {
          this.textAlert = this.translateService.instant(this.i18n.errorUploadingFile);
        }
        this.showErrorAlert = true;
        this.setErrorAlertAutoClose();
        // Se marca en rojo en la lista, con el motivo: reintentarlo tal cual
        // volvería a fallar, y la alerta se auto-cierra a los 5 segundos.
        this.files = this.files.filter(f => f !== file);
        this.rejected = [
          ...this.rejected,
          { name: file.name, size: file.size, type: file.type, content: this.textAlert },
        ];
        return;
      }
    }

    this.loader.hide();
    this.closeWithResult();
  }

  onErrorAlertClosed(): void {
    if (this.errorAlertTimeout) {
      clearTimeout(this.errorAlertTimeout);
      this.errorAlertTimeout = null;
    }
    this.isClosingAlert = true;
    setTimeout(() => {
      this.showErrorAlert = false;
      this.isClosingAlert = false;
      this.textAlert = '';
    }, 400);
  }

  private todayFormatted(): string {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  private showAlert(key: string): void {
    this.textAlert = this.translateService.instant(key);
    this.showErrorAlert = true;
    this.setErrorAlertAutoClose();
  }

  private setErrorAlertAutoClose(): void {
    if (this.errorAlertTimeout) {
      clearTimeout(this.errorAlertTimeout);
    }
    this.errorAlertTimeout = setTimeout(() => {
      this.onErrorAlertClosed();
    }, 5000);
  }

  private closeWithResult(): void {
    const selectedDocuments = this.savedFiles.filter(f => f.checked);

    let deliveryNoteResult: DeliveryNoteModalResponse | null = null;
    if (this.addedFiles.length > 0) {
      const first = this.addedFiles[0];
      deliveryNoteResult = {
        documentId: first.documentId,
        documentName: first.name,
        uploadedDate: first.uploadedDate || this.todayFormatted(),
        documentSize: first.fileSize,
      };
    } else if (this.addedLinks.length > 0) {
      const first = this.addedLinks[0];
      deliveryNoteResult = {
        documentId: first.documentId,
        documentName: first.name,
        uploadedDate: first.uploadedDate || this.todayFormatted(),
        documentSize: first.fileSize,
      };
    } else if (selectedDocuments.length > 0) {
      const first = selectedDocuments[0];
      deliveryNoteResult = {
        documentId: first.documentId,
        documentName: first.name,
        uploadedDate: first.uploadedDate || this.todayFormatted(),
        documentSize: first.fileSize,
      };
    }

    this.activeModal.close({
      deliveryNoteResult,
      selectedDocuments,
      addedFiles: this.addedFiles,
      addedLinks: this.addedLinks,
    });
  }

  private loadSavedFiles(): void {
    this.loader.show();
    this.deliveryNoteService
      .getAttachedFilesAndLinks()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loader.hide())
      )
      .subscribe({
        next: (docs) => {
          this.savedFiles = docs.map(d => this.deliveryNoteService.mapToDeliveryNoteDocument(d));
          this.originalSavedFiles = [...this.savedFiles];
        },
        error: () => {
          this.textAlert = this.translateService.instant(this.i18n.errorFetchingDocuments);
          this.showErrorAlert = true;
        },
      });
  }
}
